import {
    MAX_ROW_LIMIT,
    buildResource,
    fetchDailySeries,
    fetchRowChunk,
    runQueries,
} from '../GscTools/query';
import { CONTRIBUTOR_DIMENSION } from './scope';
import { topicFilters } from './topics';

/**
 * The requests this report makes, one function per block.
 *
 * Every one of them is a query-filtered ask: a topic's filters go into the request's "and" group,
 * alongside the scope's own device and country, and nothing is derived by subtracting one topic
 * from another or from an unfiltered total. A topic is only ever its own response — and never the
 * property's traffic, because Search Console withholds queries below its own thresholds and those
 * are in no topic at all.
 *
 * The totals and the trend are aggregated by property: the topic is a query scope, and by-property
 * is what keeps one search result showing two of the site's pages from counting twice. Block 04
 * is the exception, and says so — its pages view is by page, because that is what a page row is.
 */

const AGGREGATION = 'byProperty';

/** The parts of the scope every request on this report carries, besides the topic's filters. */
function scopeRequest(scope) {
    return {
        searchType: scope.searchType,
        country: scope.country,
        device: scope.device,
        dataState: scope.dataState,
    };
}

/**
 * Block 02 — one topic's totals for each window, asked for with no dimension at all.
 *
 * One call per topic, holding both of its windows, so topics fail independently. The baseline is
 * left out when there is no comparison rather than sent and ignored, and only the topics the
 * session defined are ever asked for.
 */
export async function fetchTopicTotals({ credentialsDomainId, scope, topic }) {
    const filters = topicFilters(topic);

    const queries = Object.entries({ current: scope.current, baseline: scope.baseline })
        .filter(([, period]) => !!period)
        .map(([key, period]) => ({
            key,
            resource: buildResource({
                period,
                dimensions: [],
                aggregationType: AGGREGATION,
                ...scopeRequest(scope),
                filters,
            }),
        }));

    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries,
    });

    return { current: results.current ?? [], baseline: results.baseline ?? [] };
}

/**
 * Block 03 — the active topic's daily rows for one window, with the identical filters the
 * comparison used. Each window is its own call, so a baseline that fails leaves the current line
 * drawn.
 */
export function fetchTopicDaily({ credentialsDomainId, scope, topic, period }) {
    return fetchDailySeries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        period,
        ...scopeRequest(scope),
        filters: topicFilters(topic),
        aggregationType: AGGREGATION,
    });
}

/**
 * Block 04 — one chunk of the active topic's queries or pages, for the detail window.
 *
 * Paged through the shared bounded retrieval, whose `auto` aggregation is what the two views each
 * need: by property for a query dimension and by page for a page dimension. What comes back is the
 * rows Search Console was willing to return under the topic's filters — never an inventory.
 */
export function fetchTopicContributors({
    credentialsDomainId,
    scope,
    topic,
    period,
    view,
    offset = 0,
}) {
    return fetchRowChunk({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        period,
        dimension: CONTRIBUTOR_DIMENSION[view],
        ...scopeRequest(scope),
        filters: topicFilters(topic),
        offset,
    });
}
